import { useEffect, useRef } from 'react';
import { type SelfDescribingJson } from '@ev-tech/snowplow-library';

import { useDidomi } from '@/providers/DidomiContextProvider';

type UseTrackPageViewOptions = {
  debounceMs?: number;
  isReady?: boolean;
  trackingKey?: string | number;
};

export const useTrackPageView = (
  contexts: SelfDescribingJson[],
  { debounceMs = 500, isReady = true, trackingKey }: UseTrackPageViewOptions = {},
) => {
  const didomi = useDidomi();
  const hasTrackedPageView = useRef(false);
  const lastTrackedViewKey = useRef<string | number | undefined>(undefined);

  useEffect(() => {
    const hasTrackedCurrentView = trackingKey !== undefined ? lastTrackedViewKey.current === trackingKey : hasTrackedPageView.current;

    if (hasTrackedCurrentView || !isReady || !didomi.isSnowplowInitialized || !didomi.isReady) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      const hasTrackedCurrentViewAtTimeout =
        trackingKey !== undefined ? lastTrackedViewKey.current === trackingKey : hasTrackedPageView.current;

      if (hasTrackedCurrentViewAtTimeout) return;

      didomi.trackPageView(contexts);
      hasTrackedPageView.current = true;
      lastTrackedViewKey.current = trackingKey;
    }, debounceMs);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [contexts, debounceMs, didomi, didomi.isReady, didomi.isSnowplowInitialized, isReady, trackingKey]);
};
